import { cloneDeep, orderBy } from "lodash";
import { createContext, Dispatch, JSX, ReactNode, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { RoleType, UserService } from "@app/user";
import { SortDirections } from "@app/shared/waffles";
import { NewUserModel, UserAuditTrailModel, UserModelFromApi, UserModelToApi, UserStatus } from "../user-administration/userModel";
import { Client, ClientRoleModel, ClientService } from "@app/client";
import { isEqual } from "lodash";
import { GuestLoginAdminModel } from "../../user/guestLogin";
import { RoleGroupModel, RoleGroupService } from "@app/admin/role-groups";

type AdminUsersContextProps = {
  userId?: number;
} & { children: ReactNode };

export interface AdminUsersProps {
  // API calls
  checkUserNameAvailability: (newUserName: string) => void;
  checkEmailAvailability: (newEmail: string) => void;
  createUser: () => Promise<void>;
  getAllUsers: (skip?: number, take?: number, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[], sortDirection?: SortDirections, sortedColumn?: string) => void;
  getClientNameByClientId: (clientId: number) => void;
  getUserCount: (formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[]) => void;
  getUsersByClient: (clientId: number, skip?: number, take?: number, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[], sortDirection?: SortDirections, sortedColumn?: string) => void;
  getUserCountByClient: (clientId: number, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[]) => void;
  updateUser: () => Promise<void>;
  updateUserNameIsSameAsEmail: void;

  resendWelcomeEmail: () => Promise<void>;
  activateUser: (isActive: boolean) => Promise<void>;
  deleteUser: (isDeleted: boolean) => Promise<void>;
  createGuestLogins: (userId?: number) => Promise<void>;
  deleteGuestLogins: () => void;
  reactivateDeletedGuestLogins: () => void;

  addGuestLogin: (clientId: number, clientName: string, roleNames: string[], roleGroupIds: number[]) => void;
  updateGuestLogin: (clientId: number, roleNames: string[], roleGroupIds: number[]) => void;

  // informational
  client: Client;

  // For the Users List
  userCount: number;
  users: UserModelFromApi[];

  // Getters for User Creation/Details
  address: string;
  allClients: Client[];
  allowedDomains: string[];
  allowedRoleGroups: RoleGroupModel[];
  allowedRoles: ClientRoleModel[];
  city: string;
  clientId: number;
  clientName: string;
  defaultUserRoles: ClientRoleModel[];
  deletedGuestLogins: GuestLoginAdminModel[];
  email: string;
  emailConfirmed: boolean;
  firstName: string;
  resourceAdminGuestLoginsAllowed?: boolean;
  lastName: string;
  passwordExpirationEnabled: boolean;
  loginPermitted: boolean;
  middleInitial: string;
  phoneNumber: string;
  roleGroups: RoleGroupModel[];
  roles: ClientRoleModel[];
  sFContactId: string;
  selectedClients: string[];
  state: string;
  questViewerGuestLoginsAllowed?: boolean;
  userId: number;
  userName: string;
  zip: string;
  guestLogins: GuestLoginAdminModel[];
  auditTrail: UserAuditTrailModel[];
  status: UserStatus;
  userNameIsSameAsEmail: boolean;

  // Setters for User Creation/Details
  setAddress: Dispatch<SetStateAction<string>>;
  setCity: Dispatch<SetStateAction<string>>;
  setClientId: Dispatch<SetStateAction<number>>;
  setClientName: Dispatch<SetStateAction<string>>;
  setDefaultUserRoles: Dispatch<SetStateAction<ClientRoleModel[]>>;
  setDeletedGuestLogins: Dispatch<SetStateAction<GuestLoginAdminModel[]>>;
  setEmail: Dispatch<SetStateAction<string>>;
  setEmailConfirmed: Dispatch<SetStateAction<boolean>>;
  setFirstName: Dispatch<SetStateAction<string>>;
  setLastName: Dispatch<SetStateAction<string>>;
  setPasswordExpirationEnabled: Dispatch<SetStateAction<boolean>>;
  setLoginPermitted: Dispatch<SetStateAction<boolean>>;
  setGuestLogins: Dispatch<SetStateAction<GuestLoginAdminModel[]>>;
  setResourceAdminGuestLoginsAllowed: Dispatch<SetStateAction<boolean>>;
  setQuestViewerGuestLoginsAllowed: Dispatch<SetStateAction<boolean>>;
  setMiddleInitial: Dispatch<SetStateAction<string>>;
  setPhoneNumber: Dispatch<SetStateAction<string>>;
  setRoleGroups: Dispatch<SetStateAction<RoleGroupModel[]>>;
  setRoles: Dispatch<SetStateAction<ClientRoleModel[]>>;
  setSFContactId: Dispatch<SetStateAction<string>>;
  setSelectedClients: Dispatch<SetStateAction<string[]>>;
  setState: Dispatch<SetStateAction<string>>;
  setStatus: Dispatch<SetStateAction<UserStatus>>;
  setUserId: Dispatch<SetStateAction<number>>;
  setUserName: Dispatch<SetStateAction<string>>;
  setZip: Dispatch<SetStateAction<string>>;
  setUserNameIsSameAsEmail: Dispatch<SetStateAction<boolean>>;

  // validation
  changedFields: () => string[];
  formIsValid: boolean;
  fieldNameToVisibleName: { [key: string]: string };
  loadingUser: boolean;
  requiredFields: { [key: string]: boolean };
  validationFunctions: { [key: string]: () => boolean };
  validationObject: { [requiredKey: string]: boolean };
}

export const AdminUsersContext = createContext<Partial<AdminUsersProps>>({});

const clientService = new ClientService();
const userService = new UserService();
const roleGroupService = new RoleGroupService();

export const AdminUsersProvider = (props: AdminUsersContextProps): JSX.Element => {
  const [userId, setUserId] = useState(props.userId);
  const [userName, setUserName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleInitial, setMiddleInitial] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [email, setEmail] = useState("");
  const [sFContactId, setSFContactId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [roleGroups, setRoleGroups] = useState<RoleGroupModel[]>([]);
  const [roles, setRoles] = useState<ClientRoleModel[]>([]);
  const [clientId, setClientId] = useState(-1);
  const [clientName, setClientName] = useState("");
  const [users, setUsers] = useState<UserModelFromApi[]>([]);
  const [userCount, setUserCount] = useState(0);
  const [loadingUser, setLoadingUser] = useState(true);
  const [status, setStatus] = useState<UserStatus>(UserStatus.UNKNOWN);

  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [passwordExpirationEnabled, setPasswordExpirationEnabled] = useState(false);
  const [loginPermitted, setLoginPermitted] = useState(false);
  const [resourceAdminGuestLoginsAllowed, setResourceAdminGuestLoginsAllowed] = useState(false);
  const [questViewerGuestLoginsAllowed, setQuestViewerGuestLoginsAllowed] = useState(false);

  const [allowedRoleGroups, setAllowedRoleGroups] = useState<RoleGroupModel[]>([]);
  const [allowedRoles, setAllowedRoles] = useState<ClientRoleModel[]>([]);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [defaultUserRoles, setDefaultUserRoles] = useState<ClientRoleModel[]>([]);

  const [guestLogins, setGuestLogins] = useState<GuestLoginAdminModel[]>([]);
  const [deletedGuestLogins, setDeletedGuestLogins] = useState<GuestLoginAdminModel[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [auditTrail, setAuditTrail] = useState<UserAuditTrailModel[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [client, setClient] = useState<Client>();

  const [isUserNameAvailable, setIsUserNameAvailable] = useState(true);
  const [isEmailAvailable, setIsEmailAvailable] = useState(true);
  const initialUser = useRef<UserModelFromApi>(null);
  const [validationObject, setValidationObject] = useState<{ [key: string]: boolean }>({});
  const [userNameIsSameAsEmail, setUserNameIsSameAsEmail] = useState(true);

  useEffect(() => {
    if (userId) {
      userService.getUser(userId).then((user) => {
        const sortedRoleGroups = orderBy(user.roleGroups, (r) => r.name.toLowerCase(), "asc");
        const sortedRoles = orderBy(user.roles.map<ClientRoleModel>((r) => ({ ...r, questViewerGuestDefault: null, resourceAdminGuestDefault: null, userDefault: null })), (r) => r.name.toLowerCase(), "asc");
        const sortedGuestLogins = orderBy(user.guestLogins, (r) => r.clientName.toLowerCase(), "asc");
        const sortedDeletedGuestLogins = orderBy(user.deletedGuestLogins, (r) => r.clientName.toLowerCase(), "asc");

        setUserName(user.userName || "");
        setFirstName(user.firstName || "");
        setMiddleInitial(user.middleInitial || "");
        setLastName(user.lastName || "");
        setAddress(user.address || "");
        setCity(user.city || "");
        setState(user.state || "");
        setZip(user.zip || "");
        setEmail(user.email?.toLowerCase() || "");
        setSFContactId(user.sFContactId || "");
        setPhoneNumber(user.phoneNumber || "");
        setRoleGroups(sortedRoleGroups);
        setRoles(sortedRoles);
        setClientId(user.clientId);
        setClientName(user.clientName || "");
        setSFContactId(user.sFContactId || "");

        setUserNameIsSameAsEmail((user.email?.toLowerCase() || "") === (user.userName.toLowerCase() || ""));

        setEmailConfirmed(user.emailConfirmed);
        setPasswordExpirationEnabled(user.passwordExpirationEnabled || false);
        setLoginPermitted(user.loginPermitted);

        setResourceAdminGuestLoginsAllowed(user.resourceAdminGuestLoginsAllowed || false);
        setQuestViewerGuestLoginsAllowed(user.questViewerGuestLoginsAllowed || false);

        setStatus(user.status);

        setGuestLogins(sortedGuestLogins);
        setDeletedGuestLogins(sortedDeletedGuestLogins);
        setAuditTrail(user.auditTrail);

        setLoadingUser(false);

        initialUser.current = {
          ...user,
          deletedGuestLogins: cloneDeep(sortedDeletedGuestLogins),
          guestLogins: cloneDeep(sortedGuestLogins),
          questViewerGuestLoginsAllowed: user.questViewerGuestLoginsAllowed ?? false,
          resourceAdminGuestLoginsAllowed: user.resourceAdminGuestLoginsAllowed ?? false,
          roleGroups: sortedRoleGroups,
          roles: cloneDeep(sortedRoles),
          userNameIsSameAsEmail: (user.email?.toLowerCase() || "") === (user.userName.toLowerCase() || "")
        };
      });
    } else {
      // user creation under specific clients will default the user's roles to the client defaults if not already defined
      if (clientId !== -1) {
        clientService.getClientRoles(clientId).then((newAllowedRoles) => {
          const defaultRoles = orderBy(newAllowedRoles.filter((ar) => ar.userDefault), "asc").map<ClientRoleModel>((r) => ({ ...r, questViewerGuestDefault: null, resourceAdminGuestDefault: null, userDefault: null }));
          setRoles(defaultRoles);
        });
      }
      setLoadingUser(false);
    }
  }, [userId, clientId]);

  useEffect(() => {
    if (clientId !== -1) {
      clientService.getClient(clientId).then((client) => {
        setAllowedDomains(client.domains.map((d) => d.domain.toLowerCase()));
        let newAllowedRoles = client.roles;

        // if this isn't a Quest client user, only show public roles (internal role visibility is managed on the guest logins UI)
        if (!client.isQuestInternal)
          newAllowedRoles = newAllowedRoles.filter(r => r.roleType === RoleType.Public);

        setAllowedRoles(orderBy(newAllowedRoles, (n) => n.name.toLowerCase(), "asc"));
        const defaultRoles = orderBy(newAllowedRoles.filter((ar) => ar.userDefault), "asc");
        setDefaultUserRoles(defaultRoles);

        setClient(client);
      });

      roleGroupService.getRoleGroupsByClientId(clientId, true, false, false, true, 0, -1).then((newAllowedRoleGroupsList) => {
        setAllowedRoleGroups(orderBy(newAllowedRoleGroupsList.roleGroupList, (n) => n.name.toLowerCase(), "asc"));
      });
    }

    clientService.getAllClients().then((clients) => setAllClients(orderBy(clients, (c) => c.name.toLowerCase(), "asc")));
  }, [clientId]);

  useEffect(() => {
    if (clientId !== -1) {
      roleGroupService.getRoleGroupsByClientId(clientId, true, false, false, true, 0, -1).then((newAllowedRoleGroupsList) => {
        setAllowedRoleGroups(orderBy(newAllowedRoleGroupsList.roleGroupList, (n) => n.name.toLowerCase(), "asc"));
      });
    }
  }, [clientId]);

  const checkUserNameAvailability = (newUserName: string) => {
    if (newUserName.toLowerCase() === (initialUser.current?.userName.toLowerCase() || "")) {
      // the endpoint will return false because A USER (this one) has that username already
      setIsUserNameAvailable(true);
    } else if (newUserName === "") {
      // Returns 500 from the API
      setIsUserNameAvailable(false);
    } else {
      userService.isUsernameAvailable(newUserName).then((isAvailable) => {
        setIsUserNameAvailable(isAvailable);
      });
    }
  }

  const checkEmailAvailability = (newEmail: string) => {
    if (newEmail === initialUser.current?.email) {
      // the endpoint will return false because A USER (this one) has that username already
      setIsEmailAvailable(true);
    } else if (newEmail === "") {
      // Returns 500 from the API
      setIsEmailAvailable(false);
    } else {
      userService.isEmailAvailable(newEmail, clientId).then((isAvailable) => {
        setIsEmailAvailable(isAvailable);
      });
    }
  }

  const requiredFields: { [key: string]: boolean } = {
    "email": true,
    "firstName": true,
    "lastName": true,
    "roles": true,
    "userName": userId > 0,
  };

  const fieldNameToVisibleName: { [key: string]: string } = {
    "email": "Email",
    "firstName": "First Name",
    "lastName": "Last Name",
    "roles": "Roles",
    "userName": "Username",
  }

  const validationFunctions: { [key: string]: () => boolean } = {
    "email": () => {
      if (isValidEmailAddress(email)) {

        const domain = email.split("@")[1];
        const isValid = isEmailAvailable && (allowedDomains.includes(domain) || domain === "questanalytics.com" || domain === "questanalytics.net");

        // if username looks like an email, it must match this email exactly
        if (isValid && userName.length > 0 && isValidEmailAddress(userName))
          return email.toLowerCase() === userName.toLowerCase();
        else
          return isValid;
      }
      else
        return false;
    },
    "firstName": () => !!firstName,
    "lastName": () => !!lastName,
    "roles": () => {
      return (roleGroups.length > 0 || roles.length > 0) && allowedRoles.length > 0 && roles.every((r) => allowedRoles.find((ar) => ar.roleId === r.roleId));
    },
    "userName": () => {
      // userName can't be empty and can't already be in use
      const isValid = userName.length > 0 && isUserNameAvailable;

      // if username looks like an email, it must match the email exactly
      if (isValid && isValidEmailAddress(userName))
        return email.toLowerCase() === userName.toLowerCase();
      else
        return isValid;
    }
  }

  const isValidEmailAddress = (emailToTest: string): boolean => {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(emailToTest.replace(/(-Disable)$/, "").toLowerCase());
  }

  const updateUserNameIsSameAsEmail = useMemo(() => {
    setUserNameIsSameAsEmail((email.toLowerCase() || "") === (userName.toLowerCase() || ""));
  }, [email, userName]);

  const formIsValid = useMemo(() => {
    const validation: { [key: string]: boolean } = {};
    const requiredKeys = Object.entries(requiredFields).filter((field) => field[1]).map((field) => field[0]);
    requiredKeys.forEach((field) => Object.assign(validation, { [field]: validationFunctions[field]() }));
    setValidationObject(validation);

    return Object.values(validation).every((item) => item);
  }, [
    email,
    isEmailAvailable,
    firstName,
    lastName,
    roleGroups,
    roles,
    userName,
    isUserNameAvailable,
    allowedRoleGroups,
    allowedRoles,
    allowedDomains,
  ]);

  const changedFields = () => {
    if (!initialUser.current) {
      return [];
    }

    const userModel: UserModelFromApi = {
      address,
      auditTrail,
      city,
      clientId,
      clientName,
      deletedGuestLogins,
      email,
      emailConfirmed,
      firstName,
      guestLogins,
      lastName,
      loginPermitted,
      middleInitial,
      passwordExpirationEnabled,
      phoneNumber,
      questViewerGuestLoginsAllowed,
      resourceAdminGuestLoginsAllowed,
      roleGroups,
      roles,
      sFContactId,
      state,
      status,
      userId,
      userName,
      userNameIsSameAsEmail: userNameIsSameAsEmail || (userName.toLowerCase() === email.toLowerCase()),
      zip,
    };

    const userKeys = Object.keys(userModel);
    // if the initialUser model from the api just doesn't have a key from the UI model,
    // it's always going to return that it was changed (from undefined to "" or whatever)
    const keyDiff = userKeys.filter((uk) => !initialUser.current.hasOwnProperty(uk));
    keyDiff.forEach((key) => {
      initialUser.current = {
        ...initialUser.current,
        [key]: "",
      };
    });

    const changed: string[] = [];
    userKeys.forEach((key) => {
      if (!isEqual(userModel[key as keyof UserModelFromApi], initialUser.current[key as keyof UserModelFromApi])) {
        //console.log("dirty field:", key, userModel[key as keyof UserModelFromApi], initialUser.current[key as keyof UserModelFromApi]);
        changed.push(key);
      }
    });
    return changed;
  }

  const createUser = async () => {
    const userModel: NewUserModel = {
      address: address,
      city: city,
      clientId: clientId,
      clientName: clientName,
      email: email,
      firstName: firstName,
      guestLogins,
      lastName: lastName,
      middleInitial: middleInitial,
      phoneNumber: phoneNumber,
      questViewerGuestLoginsAllowed,
      resourceAdminGuestLoginsAllowed,
      roleGroupIds: roleGroups.map((roleGroup) => roleGroup.roleGroupId),
      roleNames: roles.map((role) => role.name),
      sFContactId: sFContactId,
      state: state,
      userName: userName,
      zip: zip,
    };

    await userService.createUser(userModel);
  }

  const resendWelcomeEmail = async () => {
    await userService.resendWelcomeEmail(userId);
  }

  const createGuestLogins = async (parentUserId?: number) => {
    await userService.createGuestLogins(parentUserId ?? userId);
  }

  const deleteGuestLogins = (): void => {
    const activeLogins = cloneDeep(guestLogins);
    const deletedLogins = cloneDeep(deletedGuestLogins);
    selectedClients.forEach((clientId) => {
      const activeGuestLogin = guestLogins.find((gl) => gl.clientId === +clientId);
      if (!!activeGuestLogin) {
        const activeLogin = activeLogins.find((gl) => gl.clientId === +clientId);
        activeLogin.loginPermitted = false;

        activeLogins.splice(activeLogins.indexOf(activeLogin), 1);
        deletedLogins.push(activeLogin);

      }
    });
    setSelectedClients([]);
    const sortedDeletedLogins = orderBy(deletedLogins, (l) => l.clientName.toLowerCase(), "asc");
    setDeletedGuestLogins(sortedDeletedLogins);
    setGuestLogins(activeLogins);
  }

  const reactivateDeletedGuestLogins = (): void => {
    const activeLogins = cloneDeep(guestLogins);
    const deletedLogins = cloneDeep(deletedGuestLogins);
    selectedClients.forEach((clientId) => {
      const deletedGuestLogin = deletedGuestLogins.find((gl) => gl.clientId === +clientId);
      if (!!deletedGuestLogin) {
        const deletedLogin = deletedLogins.find((gl) => gl.clientId === +clientId);
        deletedLogin.loginPermitted = true;

        deletedLogins.splice(deletedLogins.indexOf(deletedLogin), 1);
        activeLogins.push(deletedLogin);
      }
    });
    setSelectedClients([]);
    setDeletedGuestLogins(deletedLogins);
    const sortedActiveLogins = orderBy(activeLogins, (l) => l.clientName.toLowerCase(), "asc");
    setGuestLogins(sortedActiveLogins);
  }

  const activateUser = async (isActive: boolean) => {
    await userService.activateUser(userId, isActive);
  }

  const deleteUser = async (isDeleted: boolean) => {
    await userService.deleteUser(userId, isDeleted);
  }

  const addGuestLogin = (clientId: number, clientName: string, roleNames: string[], roleGroupIds: number[]) => {
    const newGuestLogins = guestLogins.concat([{ clientId: clientId, clientName: clientName, loginPermitted: true, roleGroupIds: roleGroupIds, roleNames: roleNames }])
    const sortedGuestLogins = orderBy(newGuestLogins, (r) => r.clientName.toLowerCase(), "asc");
    setGuestLogins(sortedGuestLogins);
  }

  const updateGuestLogin = (clientId: number, roleNames: string[], roleGroupIds: number[]) => {
    guestLogins.find((g) => g.clientId == clientId).roleNames = roleNames;
    guestLogins.find((g) => g.clientId == clientId).roleGroupIds = roleGroupIds;

    const sortedGuestLogins = orderBy(guestLogins, (r) => r.clientName.toLowerCase(), "asc");
    setGuestLogins(sortedGuestLogins);
  }
  const updateUser = async () => {
    const selectedRoleGroupIds = roleGroups.map((rg) => rg.roleGroupId); // /can't be empty
    const initialRoleGroupIds = initialUser.current?.roleGroups.map((iurg) => iurg.roleGroupId) || [];
    const addedRoleGroups = selectedRoleGroupIds.filter((srg) => !initialRoleGroupIds.includes(srg));
    const removedRoleGroups = initialRoleGroupIds.filter((irg) => !selectedRoleGroupIds.includes(irg));

    const selectedRoleNames = roles.map((r) => r.name); // /can't be empty
    const initialRoleNames = initialUser.current?.roles.map((iur) => iur.name) || [];
    const addedRoles = selectedRoleNames.filter((sr) => !initialRoleNames.includes(sr));
    const removedRoles = initialRoleNames.filter((ir) => !selectedRoleNames.includes(ir));

    // handle both new additions, deletions, and restores of previously-deleted guest logins
    // * any guest login that was removed from the deleted list and appears on the active list is restored
    // * any guest login that was on the active list and is now on the deleted list is deleted
    // * any guest login that was not on the deleted list and was added to the active list is new
    // * any matching active login with role changes
    const guestLoginChanges = guestLogins.filter((gl) => {
      const matchingClient = initialUser.current.guestLogins.find((iugl) => iugl.clientId === gl.clientId);
      // restored a previously-deleted guest login
      if (initialUser.current.deletedGuestLogins.find((dgl) => dgl.clientId === gl.clientId)) {
        return true;
      }
      // changed roles on an existing guest login
      if (matchingClient) {
        // if there's a match, we need to check if the roles or role groups were changed
        return !isEqual(matchingClient.roleNames, gl.roleNames) || !isEqual(matchingClient.roleGroupIds, gl.roleGroupIds);
      }

      // if there's no match, it's a new guest login
      return true;
    }).concat(deletedGuestLogins.filter((dgl) => {
      const matchingClient = initialUser.current.guestLogins.find((gl) => gl.clientId === dgl.clientId);
      // deleted an active guest login
      return matchingClient ? true : false;
    }));

    const userModel: UserModelToApi = {
      addedRoleGroupIds: addedRoleGroups,
      addedRoleNames: addedRoles,
      address,
      changedGuestLogins: guestLoginChanges,
      city,
      clientId,
      clientName,
      email,
      emailConfirmed,
      firstName,
      lastName,
      loginPermitted,
      middleInitial,
      passwordExpirationEnabled,
      phoneNumber,
      questViewerGuestLoginsAllowed,
      removedRoleGroupIds: removedRoleGroups,
      removedRoleNames: removedRoles,
      resourceAdminGuestLoginsAllowed,
      sFContactId,
      state,
      userId,
      userName: userNameIsSameAsEmail ? email : userName,
      userNameIsSameAsEmail,
      zip,
    };
    await userService.updateUser(userModel);
  }

  const getAllUsers = (skip?: number, take?: number, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[], sortDirection?: SortDirections, sortedColumn?: string) => {
    userService.getAllUsers(skip, take, formalNameSearch, userNameSearch, emailSearch, clientNameSearch, userStatusSearch, sortDirection, sortedColumn).then((response) => setUsers(response));
  }

  const getUserCount = (formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[]): void => {
    userService.getUserCount(formalNameSearch, userNameSearch, emailSearch, clientNameSearch, userStatusSearch).then((count) => setUserCount(count));
  }

  const getUsersByClient = (clientId: number, skip?: number, take?: number, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[], sortDirection?: SortDirections, sortedColumn?: string) => {
    userService.getUsersByClient(clientId, skip, take, formalNameSearch, userNameSearch, emailSearch, clientNameSearch, userStatusSearch, sortDirection, sortedColumn).then((response) => setUsers(response));
  }

  const getUserCountByClient = (clientId: number, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[]): void => {
    userService.getUserCountByClient(clientId, formalNameSearch, userNameSearch, emailSearch, clientNameSearch, userStatusSearch).then((count) => setUserCount(count));
  }

  const getClientNameByClientId = (clientId: number) => {
    clientService.getClient(clientId).then((client) => setClientName(client.name));
  }

  return (
    <AdminUsersContext.Provider
      value={{
        activateUser,
        addGuestLogin,
        address,
        allClients,
        allowedDomains,
        allowedRoleGroups,
        allowedRoles,
        auditTrail,
        changedFields,
        checkEmailAvailability,
        checkUserNameAvailability,
        city,
        client,
        clientId,
        clientName,
        createGuestLogins,
        createUser,
        defaultUserRoles,
        deleteGuestLogins,
        deleteUser,
        deletedGuestLogins,
        email,
        emailConfirmed,
        fieldNameToVisibleName,
        firstName,
        formIsValid,
        getAllUsers,
        getClientNameByClientId,
        getUserCount,
        getUserCountByClient,
        getUsersByClient,
        guestLogins,
        lastName,
        loadingUser,
        loginPermitted,
        middleInitial,
        passwordExpirationEnabled,
        phoneNumber,
        questViewerGuestLoginsAllowed,
        reactivateDeletedGuestLogins,
        requiredFields,
        resendWelcomeEmail,
        resourceAdminGuestLoginsAllowed,
        roleGroups,
        roles,
        sFContactId,
        selectedClients,
        setAddress,
        setCity,
        setClientId,
        setClientName,
        setDefaultUserRoles,
        setDeletedGuestLogins,
        setEmail,
        setEmailConfirmed,
        setFirstName,
        setGuestLogins,
        setLastName,
        setLoginPermitted,
        setMiddleInitial,
        setPasswordExpirationEnabled,
        setPhoneNumber,
        setQuestViewerGuestLoginsAllowed,
        setResourceAdminGuestLoginsAllowed,
        setRoleGroups,
        setRoles,
        setSFContactId,
        setSelectedClients,
        setState,
        setStatus,
        setUserId,
        setUserName,
        setUserNameIsSameAsEmail,
        setZip,
        state,
        status,
        updateGuestLogin,
        updateUser,
        updateUserNameIsSameAsEmail,
        userCount,
        userId,
        userName,
        userNameIsSameAsEmail,
        users,
        validationFunctions,
        validationObject,
        zip,
      }}
    >
      {props.children}
    </AdminUsersContext.Provider>
  );
};
